import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';

import { GOOGLE_MAPS_API_KEY } from '~/constants';
import { ApiAddress } from '~/typings/API';

interface MapProps {
  address: ApiAddress;
  height?: string;
  width?: string;
  margin?: string;
}

const MapContainer = styled.div<{ height: string; width: string; margin: string }>`
  position: relative;
  width: ${({ width }) => width};
  height: ${({ height }) => height};
  border-radius: 8px;
  overflow: hidden;
  margin: ${({ margin }) => margin};

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 8px;
    box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.075);
    pointer-events: none;
    z-index: 1;
  }

  .gm-style-cc,
  .gm-style a[href^='https://maps.google.com/maps'],
  .gm-style a[href^='https://www.google.com/maps'],
  .gm-style-cc div,
  .gm-style-mtc {
    display: none !important;
  }
`;

const LoadingPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background: #efebe2;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const baseMapStyles: google.maps.MapTypeStyle[] = [
  {
    featureType: 'all',
    elementType: 'labels',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'administrative',
    elementType: 'all',
    stylers: [
      {
        visibility: 'off',
      },
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'landscape',
    elementType: 'all',
    stylers: [
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'all',
    stylers: [
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'poi.attraction',
    elementType: 'all',
    stylers: [
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'poi.business',
    elementType: 'all',
    stylers: [
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'poi.government',
    elementType: 'all',
    stylers: [
      {
        color: '#dfdcd5',
      },
    ],
  },
  {
    featureType: 'poi.medical',
    elementType: 'all',
    stylers: [
      {
        color: '#dfdcd5',
      },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'all',
    stylers: [
      {
        color: '#bad294',
      },
    ],
  },
  {
    featureType: 'poi.place_of_worship',
    elementType: 'all',
    stylers: [
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'poi.school',
    elementType: 'all',
    stylers: [
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'poi.sports_complex',
    elementType: 'all',
    stylers: [
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#ffffff',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#ffffff',
      },
    ],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#fbfbfb',
      },
    ],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'transit',
    elementType: 'all',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'all',
    stylers: [
      {
        color: '#a5d7e0',
      },
    ],
  },
];

const zoomedInMapStyles: google.maps.MapTypeStyle[] = [
  {
    featureType: 'administrative',
    elementType: 'all',
    stylers: [
      {
        visibility: 'off',
      },
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'landscape',
    elementType: 'all',
    stylers: [
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'poi',
    elementType: 'all',
    stylers: [
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'poi.attraction',
    elementType: 'all',
    stylers: [
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'poi.business',
    elementType: 'all',
    stylers: [
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'poi.government',
    elementType: 'all',
    stylers: [
      {
        color: '#dfdcd5',
      },
    ],
  },
  {
    featureType: 'poi.medical',
    elementType: 'all',
    stylers: [
      {
        color: '#dfdcd5',
      },
    ],
  },
  {
    featureType: 'poi.park',
    elementType: 'all',
    stylers: [
      {
        color: '#bad294',
      },
    ],
  },
  {
    featureType: 'poi.place_of_worship',
    elementType: 'all',
    stylers: [
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'poi.school',
    elementType: 'all',
    stylers: [
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'poi.sports_complex',
    elementType: 'all',
    stylers: [
      {
        color: '#efebe2',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#ffffff',
      },
    ],
  },
  {
    featureType: 'road.highway',
    elementType: 'geometry.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#ffffff',
      },
    ],
  },
  {
    featureType: 'road.arterial',
    elementType: 'geometry.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry.fill',
    stylers: [
      {
        color: '#fbfbfb',
      },
    ],
  },
  {
    featureType: 'road.local',
    elementType: 'geometry.stroke',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'transit',
    elementType: 'all',
    stylers: [
      {
        visibility: 'off',
      },
    ],
  },
  {
    featureType: 'water',
    elementType: 'all',
    stylers: [
      {
        color: '#a5d7e0',
      },
    ],
  },
];

const formatAddressForGeocoding = (address: ApiAddress): string => {
  return [
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.postalCode,
    address.countryCode === 'US' ? 'USA' : address.countryCode,
  ]
    .filter(Boolean)
    .join(' ');
};

const darkenColor = (hex: string, percent: number): string => {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = Math.floor(num / 65536);
  const g = Math.floor((num % 65536) / 256);
  const b = num % 256;
  const darkenedR = Math.max(0, Math.floor(r * (1 - percent)));
  const darkenedG = Math.max(0, Math.floor(g * (1 - percent)));
  const darkenedB = Math.max(0, Math.floor(b * (1 - percent)));
  const result = darkenedR * 65536 + darkenedG * 256 + darkenedB;
  return `#${result.toString(16).padStart(6, '0')}`;
};

export const Map = ({ address, height = '132px', width = '400px', margin = '0' }: MapProps) => {
  const theme = useTheme();
  const { isLoaded } = useLoadScript({
    id: 'google-map-script',
    googleMapsApiKey: GOOGLE_MAPS_API_KEY,
  });

  const [center, setCenter] = useState<google.maps.LatLngLiteral | null>(null);
  const [isGeocoding, setIsGeocoding] = useState(true);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);

  useEffect(() => {
    if (!isLoaded || !address.line1 || !address.city || !address.state || !address.postalCode) {
      return;
    }

    const geocoder = new google.maps.Geocoder();
    const addressStr = formatAddressForGeocoding(address);

    geocoder.geocode({ address: addressStr }, (results, status) => {
      if (status === 'OK' && results && results[0]) {
        const location = results[0].geometry.location;
        setCenter({
          lat: location.lat(),
          lng: location.lng(),
        });
      }
      setIsGeocoding(false);
    });
  }, [isLoaded, address]);

  useEffect(() => {
    if (!mapInstance) return;

    const updateStyles = () => {
      const zoom = mapInstance.getZoom();
      if (zoom && zoom >= 16) {
        mapInstance.setOptions({ styles: zoomedInMapStyles });
      } else {
        mapInstance.setOptions({ styles: baseMapStyles });
      }
    };

    const listener = mapInstance.addListener('zoom_changed', updateStyles);
    updateStyles();

    return () => {
      google.maps.event.removeListener(listener);
    };
  }, [mapInstance]);

  const mapOptions = useMemo<google.maps.MapOptions>(
    () => ({
      disableDefaultUI: true,
      zoomControl: false,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
      styles: baseMapStyles,
      zoom: 14,
    }),
    []
  );

  const onMapLoad = useCallback((map: google.maps.Map) => {
    setMapInstance(map);
  }, []);

  const markerIcon = useMemo<google.maps.Icon | null>(() => {
    if (!isLoaded) return null;

    const primaryColor = theme.primary.background;
    const darkerColor = darkenColor(primaryColor, 0.5);

    const svgMarker = `
      <svg width="32" height="40" viewBox="0 0 32 40" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 0C7.163 0 0 7.163 0 16C0 24.837 16 40 16 40C16 40 32 24.837 32 16C32 7.163 24.837 0 16 0Z" fill="${primaryColor}"/>
        <circle cx="16" cy="16" r="6" fill="${darkerColor}"/>
      </svg>
    `;

    return {
      url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svgMarker)}`,
      scaledSize: new google.maps.Size(32, 40),
      anchor: new google.maps.Point(16, 40),
    };
  }, [isLoaded, theme.primary.background]);

  if (!isLoaded || isGeocoding || !center) {
    return (
      <MapContainer height={height} width={width} margin={margin}>
        <LoadingPlaceholder />
      </MapContainer>
    );
  }

  return (
    <MapContainer height={height} width={width} margin={margin}>
      <GoogleMap
        mapContainerStyle={{ width: '100%', height: '100%' }}
        center={center}
        options={mapOptions}
        onLoad={onMapLoad}
      >
        {markerIcon && <Marker position={center} icon={markerIcon} />}
      </GoogleMap>
    </MapContainer>
  );
};
